/**
 * growthEventsApi — thin client wrapper around the `logGrowthEvent` callable
 * (functions/src/inbound/growthEvents.ts, WL-V5 / WL-F1).
 *
 * BEST-EFFORT ONLY: this is pure funnel telemetry. A failure (rate-limit, the
 * `INBOUND_ENABLED` flag being off, a network error, …) is logged and swallowed
 * — it must NEVER surface as a user-visible error and must NEVER block the
 * caller (e.g. showing a paywall modal must not depend on this succeeding).
 *
 * `advisorUid` attribution (WL-F1, modules-loop-b.md) is deliberately NOT a
 * parameter here — the client cannot claim/forge an advisor relationship. The
 * server resolves it itself from the `tenant_members` graph
 * (`resolveAdvisorUidForMandant`) whenever `type === 'paywall_shown'` AND the
 * caller is Loop-B-beta. See growthEvents.ts module doc.
 *
 * @module api/growthEventsApi
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import logger from '../utils/logger';

/** The CLOSED whitelist a client is allowed to submit (mirrors the server Zod enum). */
export type ClientGrowthEventType = 'paywall_shown' | 'checkout_started';

/** Input for {@link logGrowthEventBestEffort}. */
export interface LogGrowthEventInput {
  type: ClientGrowthEventType;
  /** Free-text trigger label (e.g. `'kanzlei_uebergabe_export'`), max 200 chars server-side. */
  trigger?: string;
}

interface LogGrowthEventResult {
  logged: true;
}

/**
 * Fires a client-originated growth-event, best-effort. NEVER throws/rejects —
 * any failure is caught, logged at debug level, and swallowed.
 *
 * @param input the whitelisted event payload.
 * @returns a promise that always resolves (never rejects).
 */
export async function logGrowthEventBestEffort(input: LogGrowthEventInput): Promise<void> {
  try {
    const call = httpsCallable<LogGrowthEventInput, LogGrowthEventResult>(
      functions,
      'logGrowthEvent',
    );
    await call(input);
  } catch (err) {
    // Pure telemetry — a failure here must never affect the UI it was called from.
    logger.debug('logGrowthEventBestEffort: swallowed error', err);
  }
}
